import { RequestMethod, RequestName, RequestProxy, useCaller } from '~/hydration/bridges';
import type { MultipleFetchFunction, SingleFetchFunction } from '~/units/core/managers';

export const fetchBlogs: MultipleFetchFunction = async (limit = 10, offset = 0, order = '', { keyword } = {}) => {
  try {
    const query: {
      __limit: number;
      __offset: number;
      searchByKeyword?: string;
    } = {
      __limit: limit,
      __offset: offset,
    };
    if (keyword) {
      query.searchByKeyword = keyword as string;
    }

    return await useCaller(
      RequestProxy.N_AXON,
      RequestName.FETCH_BLOGS,
      'blogs',
      RequestMethod.GET,
      query,
      {},
      {
        cacheMode: 'only-speed-optimisation',
      },
    );
  } catch (e) {
    return [];
  }
};

export const fetchBlog: SingleFetchFunction = async (slug: string) => {
  try {
    return await useCaller(RequestProxy.N_AXON, RequestName.FETCH_BLOG, `blogs/${slug}`, RequestMethod.GET, {
      slug,
    });
  } catch (e) {
    return {};
  }
};

/**
 * Fetch multiple specific blogs by their slugs
 * @param slugs Array of blog slugs to fetch
 * @returns Promise with array of blog data
 */
export const fetchSpecificBlogs = async (slugs: string[]) => {
  try {
    if (!slugs || slugs.length === 0) {
      return { data: [], total: 0 };
    }

    // Filter out empty/invalid slugs
    const validSlugs = slugs.filter((slug) => slug && slug.trim());

    if (validSlugs.length === 0) {
      return { data: [], total: 0 };
    }

    // Fetch all blogs in parallel
    const blogPromises = validSlugs.map((slug) => fetchBlog(slug));
    const results = await Promise.all(blogPromises);

    // Filter out failed requests (empty objects)
    const validBlogs = results.filter((result) => result && result.data && Object.keys(result.data).length > 0);

    return {
      data: validBlogs.map((result) => result.data),
      total: validBlogs.length,
    };
  } catch (e) {
    return { data: [], total: 0 };
  }
};
